import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}
export const menuItems = [
  {
    title: "Dashboard",
    path: "/dashboard",
    icon: "/static/images/dashboard_icon.svg",
    alt: "Dashboard Icon",
    sideBarId: "DASHBOARD",
  },
  {
    title: "Manage Users",
    path: "/manage-users",
    icon: "/static/images/user_icon.svg",
    alt: "User Icon",
    sideBarId: "CUSTOMERS",
    selectedPaths: [
      "/manage-users",
      "/manage-users/edit",
      "/manage-users/detail",
    ],
  },
  {
    title: "Manage Product Listing",
    path: "/manage-products",
    icon: "/static/images/store_icon.svg",
    alt: "Product Icon",
    sideBarId: "PRODUCTS",
    selectedPaths: [
      "/manage-products",
      "/manage-products/add",
      "/manage-products/detail",
    ],
  },
  {
    title: "Manage Boost Plans",
    path: "/manage-boost-plans",
    icon: "/static/images/offer_icon.svg",
    alt: "Reward Icon",
    sideBarId: "BOOST_PLANS",
    selectedPaths: [
      "/manage-boost-plans",
      "/manage-boost-plans/add",
      "/manage-boost-plans/detail",
    ],
  },

  {
    title: "Manage Product Categories",
    path: "/manage-categories",
    icon: "/static/images/reward_icon.svg",
    alt: "Offer Icon",
    sideBarId: "CATEGORIES",
    selectedPaths: [
      "/manage-categories",
      "/manage-categories/add",
      "/manage-categories/edit",
      "/manage-categories/detail",
    ],
  },

  {
    title: "Manage Sub-Admin",
    path: "/manage-subadmin",
    icon: "/static/images/user_icon.svg",
    alt: "User Icon",
    sideBarId: "SUB_ADMIN",
    selectedPaths: [
      "/manage-subadmin",
      "/manage-subadmin/add",
      "/manage-subadmin/detail",
      "/manage-subadmin/roles",
      "/manage-subadmin/roles/add",
      "/manage-subadmin/roles/detail",
    ],
  },

  {
    title: "Manage Payments",
    path: "/manage-payments",
    icon: "/static/images/transaction_icon.svg",
    alt: "Transaction Icon",
    sideBarId: "PAYMENTS",
    selectedPaths: ["/manage-payments", "/manage-payments/view"],
  },

  {
    title: "Revenue Analytics",
    path: "/analytics",
    icon: "/static/images/report_icon.svg",
    alt: "Report Icon",
    sideBarId: "ANALYTICS",
  },
  {
    title: "Manage Customer Support",
    path: "/customer-supports",
    icon: "/static/images/settings.svg",
    alt: "Settings Icon",
    sideBarId: "SUPPORT",
    selectedPaths: [
      "/customer-supports",
      "/customer-supports/view",
      "/customer-supports/chat",
    ],
  },
  {
    title: "Notification Management",
    path: "/manage-notifications",
    icon: "/static/images/notification2_icon.svg",
    alt: "Notification Icon",
    sideBarId: "NOTIFICATION",
    selectedPaths: [
      "/manage-notifications",
      "/manage-notifications/add",
      "/manage-notifications/detail",
    ],
  },
  {
    title: "Manage Reviews",
    path: "/manage-reviews",
    icon: "/static/images/report_icon.svg",
    alt: "Report Icon",
    sideBarId: "REVIEWS",
  },

  {
    title: "Manage CMS Pages",
    path: "/manage-cms",
    icon: "/static/images/cms_icon.svg",
    alt: "CMS Icon",
    sideBarId: "CMS",
  },
];

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuth();

  // Helper function to check if a menu item is selected
  const isItemSelected = (item: any): boolean => {
    if (item.selectedPaths) {
      return item.selectedPaths.includes(location.pathname);
    }
    return location.pathname === item.path;
  };

  // Render individual menu item
  const renderMenuItem = (item: any, index: any) => (
    <Tooltip key={index} title={item.title} placement="right">
      <ListItemButton
        className="lst-itm"
        selected={isItemSelected(item)}
        onClick={() => navigate(item.path)}
      >
        <ListItemIcon>
          <img src={item.icon} alt={item.alt} />
        </ListItemIcon>
        <ListItemText className="lstitm-txt" primary={item.title} />
      </ListItemButton>
    </Tooltip>
  );

  // Build allowed menu items based on accessRole permissions for sub-admins
  const permissions = (user as any)?.accessRole?.permissions || [];
  const isSubAdmin = (user as any)?.role === 3;
  const hasViewPermission = (title: string) =>
    permissions?.some((p: any) => p?.label === title && p?.isView === true);

  const allowedMenuItems = isSubAdmin
    ? menuItems.filter((item) => hasViewPermission(item.title))
    : menuItems;

  const drawer = (
    <>
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dashboard");
          }}
          className="logo_link"
        >
          <figure>
            <img src="/static/images/logo.png" alt="MEITECH Logo" />
            {/* <figcaption>Barber Shop</figcaption>
            <figcaption className="logo_sm">BS</figcaption> */}
          </figure>
        </a>

        <IconButton
          sx={{
            display: "none",
            position: "absolute",
            top: "10px",
            right: "10px",
          }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {allowedMenuItems?.map(renderMenuItem)}
      </List>
    </>
  );
  return (
    <Box
      component="nav"
      sx={{
        width: mobileOpen ? SIDEBAR_WIDTH : 0,
        transition: "width 0.3s",
        overflowX: "hidden",
      }}
      className={mobileOpen ? "sidebar-main-div active" : "sidebar-main-div"}
    >
      <Drawer
        className="sidebar_drawer"
        variant="persistent"
        sx={{
          // display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      {/* <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer> */}
    </Box>
  );
}
